import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Label from '../../../components/ui/Label';

const SearchFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  isCollapsed, 
  onToggleCollapse,
  resultCount,
  documentBases = []
}) => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const documentTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'policy', label: 'Policy Documents' },
    { value: 'procedure', label: 'Procedures' },
    { value: 'manual', label: 'Manuals' },
    { value: 'form', label: 'Forms' },
    { value: 'training', label: 'Training Materials' },
    { value: 'compliance', label: 'Compliance Documents' }
  ];

  // Generate dynamic filter options from documentBases
  const documentBaseOptions = documentBases?.map(base => ({
    value: base?.name?.toLowerCase()?.replace(/\s+/g, '-'),
    label: base?.name
  })) || [];

  const authors = [
    { value: 'all', label: 'All Authors' },
    { value: 'john-doe', label: 'John Doe' },
    { value: 'jane-smith', label: 'Jane Smith' },
    { value: 'mike-johnson', label: 'Mike Johnson' },
    { value: 'sarah-wilson', label: 'Sarah Wilson' },
    { value: 'david-brown', label: 'David Brown' }
  ];

  const categories = [
    { value: 'administrative', label: 'Administrative', count: 45 },
    { value: 'technical', label: 'Technical', count: 32 },
    { value: 'safety', label: 'Safety', count: 28 },
    { value: 'compliance', label: 'Compliance', count: 19 },
    { value: 'training', label: 'Training', count: 15 },
    { value: 'financial', label: 'Financial', count: 12 }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleDateRangeChange = (type, value) => {
    const newDateRange = { ...dateRange, [type]: value };
    setDateRange(newDateRange);
    onFiltersChange({
      ...filters,
      dateRange: newDateRange
    });
  };

  const handleCategoryToggle = (categoryValue) => {
    const currentCategories = filters?.categories || [];
    const updatedCategories = currentCategories?.includes(categoryValue)
      ? currentCategories?.filter(cat => cat !== categoryValue)
      : [...currentCategories, categoryValue];
    
    handleFilterChange('categories', updatedCategories);
  };

  const activeFiltersCount = Object.values(filters)?.filter(value => {
    if (Array.isArray(value)) return value?.length > 0;
    if (typeof value === 'object' && value !== null) {
      return Object.values(value)?.some(v => v !== '');
    }
    return value && value !== 'all';
  })?.length;

  return (
    <div className={`bg-card border-r transition-all duration-300 ${
      isCollapsed ? 'w-0 overflow-hidden' : 'w-80'
    } lg:w-80 flex-shrink-0`}>
      <div className="p-6 space-y-6">
        {/* Filter Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Icon name="Filter" size={20} className="text-muted-foreground" />
            <h2 className="text-lg font-semibold text-card-foreground">Filters</h2>
            {activeFiltersCount > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="lg:hidden"
            iconName="X"
            iconSize={18}
          />
        </div>

        {/* Results Count */}
        <div className="mb-6 p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{resultCount}</span> documents match your criteria
          </p>
        </div>

        {/* Document Type Filter */}
        <div className="mb-6">
          <Select
            label="Document Type"
            options={documentTypes}
            value={filters?.documentType || 'all'}
            onChange={(value) => handleFilterChange('documentType', value)}
          />
        </div>

        {/* Document Base Filter */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Document Base</Label>
          <Select
            value={filters?.documentBase}
            onValueChange={(value) => handleFilterChange('documentBase', value)}
          >
            <option value="all">All Document Bases</option>
            {documentBaseOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Author Filter */}
        <div className="mb-6">
          <Select
            label="Author"
            options={authors}
            value={filters?.author || 'all'}
            onChange={(value) => handleFilterChange('author', value)}
            searchable
          />
        </div>

        {/* Date Range Filter */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-card-foreground mb-3">Date Range</h3>
          <div className="space-y-3">
            <Input
              type="date"
              label="From"
              value={dateRange?.start}
              onChange={(e) => handleDateRangeChange('start', e?.target?.value)}
            />
            <Input
              type="date"
              label="To"
              value={dateRange?.end}
              onChange={(e) => handleDateRangeChange('end', e?.target?.value)}
            />
          </div>
        </div>

        {/* Content Categories */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-card-foreground mb-3">Content Categories</h3>
          <div className="space-y-2">
            {categories?.map((category) => (
              <div key={category?.value} className="flex items-center justify-between">
                <Checkbox
                  label={category?.label}
                  checked={(filters?.categories || [])?.includes(category?.value)}
                  onChange={() => handleCategoryToggle(category?.value)}
                />
                <span className="text-xs text-muted-foreground">
                  {category?.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Clear Filters */}
        {activeFiltersCount > 0 && (
          <Button
            variant="outline"
            onClick={onClearFilters}
            className="w-full"
            iconName="X"
            iconSize={16}
          >
            Clear All Filters
          </Button>
        )}
      </div>
    </div>
  );
};

export default SearchFilters;